<template>
  <v-dialog
    v-model="show"
    max-width="600"
    overlay-color="darktext"
    overlay-opacity="0.6"
    :persistent="loading ? true : false"
    disabled
  >
    <AlertMessages
      v-if="alertValue"
      :alert-type="alertType"
      :alert-msg="alertMsg"
      @reset="alertValue = false"
    />
    <v-card v-if="searchMode" class="pa-5">
      <template v-if="!articleId && integrationType == 'sendViaVisma'">
        <v-card-title class="justify-center">
          {{ $t("selectArticleTitle") }}
        </v-card-title>

        <v-card-text class="text-center">
          {{ $t("selectArticleText") }}
        </v-card-text>
        <v-card-text>
          <v-autocomplete
            v-model="selectedArticle"
            :menu-props="{ rounded: 'xxl' }"
            :items="articleItems"
            :loading="isLoading"
            :search-input.sync="searchArticle"
            color="white"
            hide-selected
            item-text="keyword"
            item-value="id"
            :placeholder="$t('typeToSearch')"
            return-object
            clearable
            solo
            filled
            rounded
            no-filter
            class="rounded-xxl text-body-2 px-1"
            @blur="closeArticleSearch"
            autofocus
            data-testid="users-input"
            @input="setArticle"
            @change="changeArticle"
          >
            <template v-slot:no-data>
              <p class="mx-5 mt-4">
                {{ $t("noDataText") }}
              </p>
            </template>
            <template v-slot:selection="data">
              <v-chip
                v-bind="data.attrs"
                close
                data-testid="user-chip"
                @click:close="removeArticle(data.item)"
              >
                {{ data.item.Name }}
              </v-chip>
            </template>
            <template v-slot:item="data">
              <v-list-item-content>
                <v-list-item-title
                  >{{ data.item.Name }} {{ data.item.Number }}
                </v-list-item-title>
              </v-list-item-content>
            </template>
          </v-autocomplete>
        </v-card-text>
      </template>
      <template v-if="articleId || integrationType !== 'sendViaVisma'">
      <v-card-title class="justify-center">
        {{ $t("selectCustomerTitle") }}
      </v-card-title>

      <v-card-text class="text-center">
        {{ $t("selectCustomerText") }}
      </v-card-text>
      <v-card-text>
        <v-form
          ref="form"
          v-model="valid"
          @keyup.enter="submitDialogBtn()"
          @submit.prevent
        >
          <v-autocomplete
            v-model="selectedCustomer"
            :menu-props="{ rounded: 'xxl' }"
            :items="items"
            :loading="isLoading"
            :search-input.sync="search"
            color="white"
            hide-selected
            item-text="searchTerm"
            item-value="id"
            :placeholder="$t('typeToSearch')"
            return-object
            clearable
            solo
            filled
            rounded
            no-filter
            class="rounded-xxl text-body-2 px-1"
            @blur="closeSearch"
            autofocus
            data-testid="users-input"
            @input="setCustomer"
            @change="change"
          >
            <template v-slot:no-data>
              <p class="mx-5 mt-4">
                {{ $t("noDataText") }}
              </p>
            </template>
            <template v-slot:selection="data">
              <v-chip
                v-bind="data.attrs"
                close
                data-testid="user-chip"
                @click:close="remove(data.item)"
              >
                {{ data.item.Name }}
                {{ data.item.Email }}
              </v-chip>
            </template>
            <template v-slot:item="data">
              <v-list-item-content>
                <v-list-item-title>{{ data.item.Name }} </v-list-item-title>
                <v-list-item-subtitle>
                  {{ data.item.Email }}
                </v-list-item-subtitle>
              </v-list-item-content>
            </template>
          </v-autocomplete>
        </v-form>
      </v-card-text>

      <v-card-text class="text-center"> {{ $t("or") }} </v-card-text>

      <v-card-text class="text-center">
        <v-btn
          @click.stop="
            createMode = true;
            searchMode = false;
          "
          data-testid="new-customer-button"
          text
          :disabled="!articleId && !integrationType === 'sendViaVisma'"
          class="text-none no-text-transform no-hover"
          color="accent"
          >{{ $t("addNewCustomer") }}</v-btn
        >
      </v-card-text>
    </template>
      <v-card-actions>
        <v-spacer />
        <v-btn
          ref="cancel"
          data-testid="cancel-button"
          color="darktext"
          min-width="100"
          dark
          elevation="0"
          class="rounded-xxl text-none px-4 cancel"
          :disabled="loading"
          @click="show = false"
        >
          {{ $t("cancel") }}
        </v-btn>
        <v-btn
          ref="submit"
          data-testid="submit-button"
          type="submit"
          color="accent"
          min-width="100"
          dark
          elevation="0"
          class="rounded-xxl text-none px-4 submit create-template"
          :loading="loading"
          :disabled="loading || !selectedCustomer || (!articleId && !integrationType === 'sendViaVisma')"
          @click="submitDialogBtn()"
        >
          {{ (!articleId && integrationType == 'sendViaVisma') ? $t("next") : $t("approve") }}
        </v-btn>
      </v-card-actions>
    </v-card>

    <v-card v-if="createMode" class="pa-5">
      <v-card-title class="justify-center">{{
        $t("addNewCustomerTitle")
      }}</v-card-title>

      <v-card-text>
        <v-form
          ref="form"
          v-model="valid"
          @keyup.enter="submitDialogBtn()"
          @submit.prevent
        >
          <v-text-field
            v-model="customer.name"
            data-testid="customer-name-input"
            solo
            hide-details="auto"
            class="rounded-xxl text-body-2 pt-4"
            :rules="nameRules"
            :placeholder="$t('labelName')"
            @keydown.enter.prevent="focusNext($event.target)"
            clearable
          />
          <v-text-field
            v-model="customer.email"
            data-testid="customer-email-input"
            solo
            type="email"
            hide-details="auto"
            class="rounded-xxl text-body-2 pt-4"
            :rules="emailRules"
            :placeholder="$t('labelEmail')"
            @keydown.enter.prevent="focusNext($event.target)"
            clearable
          />

          <v-text-field
            v-if="integrationType !== 'sendViaVisma'"
            v-model="customer.address1"
            data-testid="customer-address-input"
            solo
            type="text"
            hide-details="auto"
            class="rounded-xxl text-body-2 pt-4"
            :placeholder="$t('labelAddress')"
            @keydown.enter.prevent="focusNext($event.target)"
          >
          </v-text-field>

          <v-text-field
            v-if="integrationType !== 'sendViaVisma'"
            v-model="customer.address2"
            data-testid="customer-address2-input"
            solo
            type="text"
            hide-details="auto"
            class="rounded-xxl text-body-2 pt-4"
            :placeholder="$t('labelAddress2')"
            @keydown.enter.prevent="focusNext($event.target)"
          >
          </v-text-field>

          <v-text-field
            v-if="integrationType == 'sendViaVisma'"
            v-model="customer.city"
            data-testid="customer-zipcode-input"
            solo
            type="text"
            hide-details="auto"
            class="rounded-xxl text-body-2 pt-4"
            :placeholder="$t('labelCity')"
            @keydown.enter.prevent="focusNext($event.target)"
          >
          </v-text-field>

          <v-text-field
            v-model="customer.zipcode"
            data-testid="customer-zipcode-input"
            solo
            type="text"
            hide-details="auto"
            class="rounded-xxl text-body-2 pt-4"
            :placeholder="$t('labelZipCode')"
            @keydown.enter.prevent="focusNext($event.target)"
          >
          </v-text-field>
        </v-form>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn
          ref="cancel"
          data-testid="cancel-button"
          color="darktext"
          min-width="100"
          dark
          elevation="0"
          class="rounded-xxl text-none px-4 cancel"
          :disabled="loading"
          @click="show = false"
        >
          {{ $t("cancel") }}
        </v-btn>
        <v-btn
          ref="submit"
          data-testid="submit-button"
          type="submit"
          color="accent"
          min-width="100"
          dark
          elevation="0"
          class="rounded-xxl text-none px-4 submit create-template"
          :loading="loading"
          :disabled="loading"
          @click="createCustomer()"
        >
          {{ $t("createAndApprove") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
<script>
import {
  ruleRequired,
  emailRuleValid,
  ruleValidMin2Max70Characters,
} from "../rules/formRules.js";
import i18n from "../plugins/i18n";
import AlertMessages from "./AlertMessages";
import {
  searchFortnoxCustomers,
  createFortnoxCustomer,
} from "../api/fortnoxApi";
import {
  searchVismaCustomers,
  createVismaCustomer,
  searchVismaArticles,
} from "../api/vismaApi";
export default {
  components: {
    AlertMessages,
  },
  props: {
    visible: Boolean,
    integrationId: {
      type: String,
      default: "",
    },
    integrationType: {
      type: String,
      default: "",
    },
    orderId: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      spaceId: window.$cookies.get("space_id"),
      customerNumber: "",
      customer: {
        name: "",
        email: "",
        zipcode: "",
        address1: "",
        address2: "",
        city: "",
      },
      valid: true,
      isLoading: false,
      alertMsg: "",
      entries: [],
      articleEntries: [],
      search: null,
      searchArticle: null,
      selectedCustomer: {},
      selectedArticle: {},
      alertType: "",
      alertValue: false,
      createMode: false,
      searchMode: true,
      articleId: "",
      loading: false,
      subdomain: "",
      domain: process.env.VUE_APP_DOMAIN,
      errorsSubdomain: [],
      subdomainRules: ruleRequired("labelSpace"),
      shareSpaceId: null,
      space: window.location.host.split(".")[0],
      dialogVisibleAddCustomers: false,

      emailRules: ruleRequired("labelEmail").concat(emailRuleValid),
      nameRules: ruleRequired("name").concat(
        ruleValidMin2Max70Characters("name")
      ),
    };
  },
  computed: {
    show: {
      get() {
        return this.visible;
      },
      set(value) {
        if (!value) {
          this.$emit("close");
        }
      },
    },
    items() {
      return this.entries.map((entry) => {
        const searchTerm = entry.Name;
        entry.Email;
        return Object.assign({}, entry, { searchTerm });
      });
    },
    articleItems() {
      return this.articleEntries.map((entry) => {
        const keyword = entry.Name;
        return Object.assign({}, entry, { keyword });
      });
    },
  },
  watch: {
    async search(val) {
      if (this.isLoading) return;
      var response;
      var entriesUsers = [];
      if (val && val.trim().length >= 2) {
        this.isLoading = true;
        try {
          if (this.integrationType == "sendViaVisma") {
            response = await searchVismaCustomers(this.integrationId, val);
          } else {
            response = await searchFortnoxCustomers(this.integrationId, val);
          }
          if (response.status == 200) {
            if (this.integrationType == "sendViaVisma") {
              entriesUsers = response.data.meta.Data;
              entriesUsers.forEach(function (item) {
                item.sort_term =
                  item.Name.toLowerCase() +
                  " " +
                  item.EmailAddress.toLowerCase();
              });
            } else {
              entriesUsers = response.data.meta.Customers;
              entriesUsers.forEach(function (item) {
                item.sort_term =
                  item.Name.toLowerCase() + " " + item.Email.toLowerCase();
              });
            }
          }
        } catch (e) {
          if (e.response) {
            this.alertValue = true;
            if (e.response.status == 403) {
              // this.$router.push({ name: "PageAccessDenied" });
              this.alertType = "error";
              this.alertMsg = i18n.t("forbiddenAccess");
            } else {
              this.alertType = "error";
              this.alertMsg = i18n.t("serverError");
            }
          }
        }

        entriesUsers.sort(function (a, b) {
          return a.sort_term > b.sort_term
            ? 1
            : b.sort_term > a.sort_term
            ? -1
            : 0;
        });
        this.entries = entriesUsers;
        this.isLoading = false;
      }
      this.isLoading = false;
    },
    async searchArticle(val) {
      if (this.isLoading) return;
      var response;
      var entriesItems = [];
      if (val && val.trim().length >= 2) {
        this.isLoading = true;
        try {
          if (this.integrationType == "sendViaVisma") {
            response = await searchVismaArticles(this.integrationId, val);

            if (response.status == 200) {
              if (this.integrationType == "sendViaVisma") {
                entriesItems = response.data.meta.Data;
                entriesItems.forEach(function (item) {
                  item.sort_term =
                    item.Name.toLowerCase() + " " + item.Number.toLowerCase();
                });
              }
            }
          }
        } catch (e) {
          if (e.response) {
            this.alertValue = true;
            if (e.response.status == 403) {
              this.alertType = "error";
              this.alertMsg = i18n.t("forbiddenAccess");
            } else {
              this.alertType = "error";
              this.alertMsg = i18n.t("serverError");
            }
          }
        }

        entriesItems.sort(function (a, b) {
          return a.sort_term > b.sort_term
            ? 1
            : b.sort_term > a.sort_term
            ? -1
            : 0;
        });
        this.articleEntries = entriesItems;
        this.isLoading = false;
      }
      this.isLoading = false;
    },
  },
  methods: {
    async submitDialogBtn() {
      this.valid = this.$refs.form.validate();
      if (
        this.valid &&
        this.errorsSubdomain.length == 0 &&
        this.customerNumber != ""
      ) {
        this.$emit("proceedSubmit", { customerNumber: this.customerNumber , method: this.integrationType , articleId: this.articleId });
        this.loading = true;
      }
    },

    change() {
      this.search = "";
    },
    changeArticle() {
      this.searchArticle = "";
    },
    remove() {
      this.selectedCustomer = {};
    },
    removeArticle() {
      this.selectedArticle = "";
    },
    closeSearch() {
      this.search = "";
    },
    closeArticleSearch() {
      this.searchArticle = "";
    },

    setCustomer() {
      if (this.integrationType == "sendViaVisma") {
        this.customerNumber = this.selectedCustomer.Id;
      } else {
        this.customerNumber = this.selectedCustomer.CustomerNumber;
      }
    },
    setArticle() {
      this.articleId = this.selectedArticle.Id;
    },
    async createCustomer() {
      var response;
      try {
        if (this.integrationType !== "sendViaVisma") {
          let data = {
            data: {
              type: "fortnox-customers",
              attributes: {
                name: this.customer.name,
                email: this.customer.email,
                zipcode: this.customer.zipcode,
                address1: this.customer.address1,
                address2: this.customer.address2,
              },
            },
          };
          response = await createFortnoxCustomer(this.integrationId, data);
          if (response.status == 200) {
            this.customerNumber = response.data.meta.Customer.CustomerNumber;
            this.$emit("proceedSubmit", { customerNumber: this.customerNumber , method: this.integrationType , articleId: this.articleId });
          }
        } else {
          let data = {
            data: {
              type: "visma-customers",
              attributes: {
                name: this.customer.name,
                email: this.customer.email,
                zipcode: this.customer.zipcode,
                address1: this.customer.address1,
                address2: this.customer.address2,
                city: this.customer.city,
              },
            },
          };

          response = await createVismaCustomer(this.integrationId, data);
          if (response.status == 200) {
            this.customerNumber = response.data.meta.Id;
            this.$emit("proceedSubmit", { customerNumber: this.customerNumber , method: this.integrationType , articleId: this.articleId });
          }
        }
      } catch (e) {
        if (e.response) {
          if (e.response.status == 403) {
            for (let i = 0; i < e.response.data.errors.length; i++) {
              if (
                e.response.data.errors[i].detail == "Member Limit is reached"
              ) {
                this.dialogVisibleApproveDenyOrder = false;
                this.alertValue = true;
                this.alertType = "error";
                this.alertMsg = i18n.t("approveOrderSpaceLimitReached");
              } else if (
                e.response.data.errors[i].detail ==
                "Participant Limit is reached"
              ) {
                this.dialogVisibleApproveDenyOrder = false;
                this.alertValue = true;
                this.alertType = "error";
                this.alertMsg = i18n.t("approveOrderProductLimitReached");
              } else {
                this.dialogVisibleApproveDenyOrder = false;
                this.alertValue = true;
                this.alertType = "error";
                this.alertMsg = i18n.t("serverError");
              }
            }
          } else if (e.response.status == 400) {
            for (let i = 0; i < e.response.data.errors.length; i++) {
              if (
                e.response.data.errors[i].detail == "Store settings not added."
              ) {
                this.alertValue = true;
                this.alertType = "error";
                this.alertMsg = i18n.t("approveOrderSettingsNotAdded");
              } else {
                this.alertValue = true;
                this.alertType = "error";
                this.alertMsg = i18n.t("serverError");
              }
            }
          } else {
            this.alertValue = true;
            this.alertType = "error";
            this.alertMsg = i18n.t("serverError");
          }
        }
      }
      this.dialogVisibleApproveDenyOrder = false;
    },
  },
};
</script>
